import { Box, Stack, styled, Theme } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import {
  AISuggestionReviewCell,
  AISuggestionReviewColumnData,
} from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewCell';
import { AISuggestionReviewLeadingCell } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewLeadingCell';
import { useAISuggestionReviewCardContext } from '@/modules/aiSuggestionReviewCard/context/aiSuggestionReviewCard.context';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';
import {
  CONTAINER_BREAKPOINTS,
  containerQuery,
} from '@/styles/containerQueries';
import { COLORS } from '@/styles/tokens/colors';

/**
 * Container wrapper that enables container queries for responsive layouts.
 */
const ResponsiveRowContainer = styled(Stack)(() => ({
  containerType: 'inline-size',
}));

/**
 * Responsive content wrapper that switches from row to column layout below the breakpoint.
 */
const ResponsiveRowContent = styled(Stack)(({ theme }: { theme: Theme }) => ({
  [containerQuery(theme).down(CONTAINER_BREAKPOINTS.RESPONSIVE_STACK)]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
}));

/**
 * Responsive wrapper for the grid content.
 */
const ResponsiveGridContent = styled(Box)(({ theme }: { theme: Theme }) => ({
  [containerQuery(theme).down(CONTAINER_BREAKPOINTS.RESPONSIVE_STACK)]: {
    width: '100%',
  },
}));

/**
 * Responsive wrapper for the actions that takes full width when stacked.
 */
const ResponsiveActionsWrapper = styled(Stack)(
  ({ theme }: { theme: Theme }) => ({
    [containerQuery(theme).down(CONTAINER_BREAKPOINTS.RESPONSIVE_STACK)]: {
      width: '100%',
      justifyContent: 'flex-end',
    },
  })
);

/**
 * Placeholder box that maintains spacing when Create button is hidden.
 * Hidden when layout is stacked since it's not needed.
 */
const WarningPlaceholder = styled(Box)(({ theme }: { theme: Theme }) => ({
  width: '70px',
  [containerQuery(theme).down(CONTAINER_BREAKPOINTS.RESPONSIVE_STACK)]: {
    display: 'none',
  },
}));

export interface AISuggestionReviewRowProps {
  /**
   * The suggestion id.
   */
  suggestionID: string;
  /**
   * Whether the row has a warning. If so, do not show the "Create" button.
   */
  rowHasWarning: boolean;
  /**
   * The data to display in the row. Only `label` and `values` are accepted —
   * `subtitle` lives on the row, not per cell, so consumers can't set it here
   * and silently get nothing.
   */
  columnsData: AISuggestionReviewColumnData[];
  /**
   * Optional subtitle rendered below the leading cell's values. When omitted,
   * the leading cell's content is vertically centered in the row.
   */
  subtitle?: string;
  /**
   * The number of columns to display. If not provided, it will be inferred from
   * the number of columns in the columnsData array.
   */
  columnCount?: number;
  /**
   * The function to call when the user clicks on the "Create" button.
   */
  onCreateClick: (suggestionID: string) => void;
  /**
   * The function to call when the user clicks on the "Review" button, or the "Magnifying Glass" (LocateSuggestionButton).
   */
  onReviewClick: (suggestionID: string) => void;
  /**
   * The function to call when the user clicks the "Delete" button.
   */
  onDeleteClick: (suggestionID: string) => void;
  /**
   * Custom create button render function. If provided, this will be used instead of the default create button.
   */
  renderCreateButton?: (params: {
    isCreating: boolean;
    isDeleting: boolean;
  }) => React.ReactNode;
  /**
   * Optional action rendered between Review and Delete.
   */
  renderAdditionalAction?: (params: { disabled: boolean }) => React.ReactNode;
}

/**
 * A row of data for an AI suggestion.
 */
export function AISuggestionReviewRow({
  suggestionID,
  rowHasWarning,
  columnsData,
  columnCount,
  subtitle,
  onCreateClick,
  onReviewClick,
  onDeleteClick,
  renderCreateButton,
  renderAdditionalAction,
}: AISuggestionReviewRowProps) {
  const {
    deletingSuggestionIds,
    creatingSuggestionIds,
    principalNodesLoading,
  } = useAISuggestionReviewCardContext();

  const isDeleting = deletingSuggestionIds.includes(suggestionID);
  const isCreating = creatingSuggestionIds.includes(suggestionID);
  // Block creation until principal nodes resolve, otherwise the entity would be
  // created without its grantors/owners (or dropped entirely).
  const createDisabled = isCreating || isDeleting || principalNodesLoading;
  // +1 for the actions column
  const numColumns = columnCount ? columnCount + 1 : columnsData.length + 1;

  return (
    <ResponsiveRowContainer
      data-testid="ai-suggestion-review-row"
      py={1.5}
      px={2}
      borderRadius="4px"
      alignSelf="stretch"
      border={`1px solid ${COLORS.GRAY['200']}`}
      bgcolor="white"
    >
      <ResponsiveRowContent direction="row" alignItems="center" gap={2}>
        <ResponsiveGridContent
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            flex: 1,
          }}
        >
          <LocateSuggestionButton
            suggestionID={suggestionID}
            onLocateSuggestionClick={onReviewClick}
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${numColumns - 1}, 1fr)`,
              gap: 2,
              flex: 1,
              alignItems: 'stretch',
            }}
          >
            {columnsData.map((column, index) =>
              index === 0 ? (
                <AISuggestionReviewLeadingCell
                  key={`${suggestionID}-${column.label}`}
                  values={column.values}
                  subtitle={subtitle}
                />
              ) : (
                <AISuggestionReviewCell
                  key={`${suggestionID}-${column.label}`}
                  label={column.label}
                  values={column.values}
                />
              )
            )}
          </Box>
        </ResponsiveGridContent>
        <ResponsiveActionsWrapper
          key={`${suggestionID}-actions`}
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="flex-end"
          flexShrink={0}
        >
          {rowHasWarning ? (
            <WarningPlaceholder />
          ) : renderCreateButton ? (
            renderCreateButton({ isCreating, isDeleting })
          ) : (
            <Button
              data-testid="create-suggestion-button"
              variant="primary"
              size="xs"
              onClick={() => onCreateClick(suggestionID)}
              loading={isCreating}
              disabled={createDisabled}
            >
              Create
            </Button>
          )}
          <Button
            data-testid="review-suggestion-button"
            variant="secondary"
            size="xs"
            onClick={() => onReviewClick(suggestionID)}
            disabled={isCreating || isDeleting}
          >
            Review
          </Button>
          <Stack direction="row" gap={0} alignItems="center">
            {renderAdditionalAction?.({ disabled: isCreating || isDeleting })}
            <IconButton
              data-testid="delete-button-prompt"
              variant="transparent"
              size="xs"
              icon={Trash01Icon}
              ariaLabel="Delete suggestion"
              onClick={() => onDeleteClick(suggestionID)}
              loading={isDeleting}
              disabled={isCreating || isDeleting}
            />
          </Stack>
        </ResponsiveActionsWrapper>
      </ResponsiveRowContent>
    </ResponsiveRowContainer>
  );
}
